import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import { FaVrCardboard } from 'react-icons/fa'
import { BiSolidCameraMovie } from 'react-icons/bi'
import { TbLicense } from 'react-icons/tb'
import { FaBook } from 'react-icons/fa6'
import { FaRocket, FaChartLine, FaMobileAlt } from 'react-icons/fa'

const vrSections = [
    {
        title: 'Virtual Training',
        icon: <FaVrCardboard className="text-3xl text-yellow-300" />,
        description:
            'Immersive VR training solutions for employees to learn, practice, and repeat skills in safe and controlled environments across healthcare, manufacturing, and customer service.'
    },
    {
        title: 'Education VR Solutions',
        icon: <FaBook className="text-3xl text-yellow-300" />,
        description:
            'Interactive learning experiences that help students and educators explore complex concepts with greater clarity, engagement, and retention.'
    },
    {
        title: 'VR Hardware & License',
        icon: <TbLicense className="text-3xl text-yellow-300" />,
        description:
            'Reliable hardware rentals, sales, setup support, and licensing options for businesses and individuals looking to deploy VR confidently.'
    },
    {
        title: 'Entertainment VR Solutions',
        icon: <BiSolidCameraMovie className="text-3xl text-yellow-300" />,
        description:
            'High-quality VR content, 360-degree videos, animations, and interactive scenarios crafted to engage audiences across marketing, education, and entertainment.'
    }
]

const stats = [
    { value: '90%', label: 'higher retention' },
    { value: 'Scalable', label: 'deployment options' },
    { value: 'Full support', label: 'installation & training' },
    { value: 'Cross-platform', label: 'VR-ready delivery' }
]

const highlights = [
    {
        title: 'Better learning outcomes',
        description: 'Give learners the opportunity to practice real scenarios with confidence and repetition in safe, immersive environments.',
        icon: <FaRocket className="text-2xl text-yellow-300" />
    },
    {
        title: 'Business-ready experiences',
        description: 'Build VR content that supports training, internal communication, tourism, product demos, and brand storytelling.',
        icon: <FaChartLine className="text-2xl text-yellow-300" />
    },
    {
        title: 'Designed for every screen',
        description: 'Every experience is optimized for smooth performance across modern headsets and presentation devices.',
        icon: <FaMobileAlt className="text-2xl text-yellow-300" />
    }
]

const VirtualReality = () => {
    const [expanded, setExpanded] = useState({})

    const toggleExpand = (idx) => {
        setExpanded((prev) => ({
            ...prev,
            [idx]: !prev[idx]
        }))
    }

    return (
        <div>
            <Helmet>
                <title>Virtual Reality | VRIFY Technologies</title>
                <meta
                    name="description"
                    content="Immersive virtual reality training, education, hardware, and entertainment solutions from VRIFY Technologies."
                />
            </Helmet>

            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 pt-16 md:pt-20 lg:pt-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.22),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.14),_transparent_20%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-10 pt-8 sm:px-6 lg:px-8 lg:pb-16">
                        <ScrollReveal>
                            <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
                                <div>
                                    <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-yellow-200">
                                        <FaVrCardboard className="text-sm" />
                                        Virtual Reality
                                    </p>
                                    <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                        Deliver immersive digital experiences that feel real, memorable, and engaging.
                                    </h1>
                                    <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                        VRIFY Technologies creates immersive VR solutions for learning, entertainment, hardware deployment, and business transformation across Africa and beyond.
                                    </p>

                                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                        <a
                                            href="/contactus"
                                            className="inline-flex items-center justify-center rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                        >
                                            Discuss your VR idea
                                        </a>
                                        <a
                                            href="#vr-solutions"
                                            className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition hover:border-yellow-300 hover:text-yellow-200"
                                        >
                                            View services
                                        </a>
                                    </div>

                                    <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                        {stats.map((stat) => (
                                            <div
                                                key={stat.label}
                                                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-sm"
                                            >
                                                <p className="text-xl font-bold text-white">{stat.value}</p>
                                                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-300">
                                                    {stat.label}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-[28px] border border-white/10 bg-slate-900/85 p-5 shadow-2xl shadow-black/30 backdrop-blur sm:p-6">
                                    <div className="rounded-[24px] bg-gradient-to-br from-yellow-400 via-amber-300 to-slate-100 p-[1px]">
                                        <div className="rounded-[23px] bg-slate-950/95 p-5 sm:p-6">
                                            <p className="text-[11px] uppercase tracking-[0.3em] text-yellow-300">What makes VR effective</p>
                                            <div className="mt-5 space-y-4">
                                                {highlights.map((item) => (
                                                    <div
                                                        key={item.title}
                                                        className="rounded-2xl border border-white/10 bg-white/5 p-4"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2">{item.icon}</div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-white">{item.title}</p>
                                                                <p className="mt-1 text-sm leading-6 text-slate-300">
                                                                    {item.description}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <section id="vr-solutions" className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-yellow-300">Service areas</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">Flexible immersive solutions that adapt to your goals.</h2>
                                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                                    We combine design, creative content, and delivery support so your VR experiences are polished from day one.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            {vrSections.map((section, idx) => (
                                <ScrollReveal key={section.title}>
                                    <div className="flex h-full flex-col rounded-[24px] border border-white/10 bg-white/5 p-5 shadow-lg shadow-black/20">
                                        <div className="flex justify-center rounded-2xl bg-slate-900/80 p-4">
                                            {section.icon}
                                        </div>
                                        <h3 className="mt-4 text-lg font-semibold text-white">{section.title}</h3>
                                        <p className="mt-3 flex-1 text-sm leading-7 text-slate-300">
                                            {expanded[idx] ? section.description : `${section.description.slice(0, 120)}...`}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(idx)}
                                            className="mt-5 inline-flex items-center justify-center rounded-full bg-yellow-400 px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                        >
                                            {expanded[idx] ? 'See Less' : 'See More'}
                                        </button>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                <div className="max-w-2xl">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-950">Immersive readiness</p>
                                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">
                                        Build world-class virtual experiences that help your team learn, showcase, and connect.
                                    </h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">
                                        From immersive training and education to entertainment and product demos, VR can create a deeper connection with your audience.
                                    </p>
                                </div>
                                <a
                                    href="/contactus"
                                    className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-200 transition hover:bg-slate-900 lg:mt-0"
                                >
                                    Launch your VR solution
                                </a>
                            </div>
                        </ScrollReveal>
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default VirtualReality